import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { updateProfile, uploadImage } from "@/lib/mapioApi";

export default function EditProfileModal({ open, onClose }) {
  const { user, profile, refreshProfile } = useAuth();
  
  const [username, setUsername] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setUsername(profile?.username || user?.username || "");
      setAvatarPreview(profile?.avatar_url || user?.avatar_url || null);
      setAvatarFile(null);
    }
  }, [open, profile, user]);

  const handleAvatarChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarFile(f);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!username.trim()) {
      toast.error("Username cannot be empty");
      return;
    }

    setLoading(true);
    try {
      let finalAvatarUrl = profile?.avatar_url || user?.avatar_url;
      
      if (avatarFile) {
        finalAvatarUrl = await uploadImage(avatarFile);
      }

      await updateProfile({
        username: username.trim(),
        avatar_url: finalAvatarUrl,
      });

      toast.success("Profile updated successfully!");
      onClose();
      refreshProfile().catch((refreshError) => {
        console.error("PROFILE REFRESH ERROR:", refreshError);
      });
    } catch (err) {
      console.error("PROFILE SAVE ERROR:", err);
      toast.error(err?.message || "Failed to update profile. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="relative px-6 pt-8 pb-4 text-center">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
              >
                <X className="w-4 h-4" />
              </button>

              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>
                Edit Profile
              </h2>
            </div>

            {/* Form */}
            <form onSubmit={handleSave} className="px-6 pb-6 space-y-4">
              <div className="flex justify-center mb-2 mt-2">
                <label className="relative cursor-pointer group">
                  <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 group-hover:border-blue-400 transition flex items-center justify-center overflow-hidden shadow-sm">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition" />
                    )}
                  </div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-md border-2 border-white">
                    <Camera className="w-4 h-4 text-white" />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-center text-xs text-gray-500 -mt-2 mb-4">
                Tap to change photo
              </p>

              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition text-sm text-gray-900 placeholder:text-gray-400 outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 text-white font-semibold text-sm shadow-md hover:shadow-lg transition disabled:opacity-60"
              >
                {loading ? "Saving…" : "Save Changes"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
